import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import SDK from 'utils/sdk';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import {
  snowflakeDownloadReport,
  snowflakeGetFirstTimers,
  snowflakeGetLocations,
  snowflakeGetStats,
  snowflakeGetTerminatedMembers,
} from './actions';
import {
  getSnowflakeDateRange,
  getSnowflakeSelectedLocation,
  getSnowflakeSelectedLocationName,
} from './selectors';

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  (typeof error === 'string' ? error : error?.message) ||
  fallback;

// When a request uses `responseType: 'blob'`, both error bodies and non-PDF
// success bodies arrive as a Blob, so the server's JSON message can't be read
// off `error.response.data` directly. Read the blob text and pull out the
// message. Returns null when there's nothing usable to surface.
const extractBlobMessage = async (blob) => {
  if (!blob || typeof blob.text !== 'function') return null;
  try {
    const text = await blob.text();
    if (!text) return null;
    try {
      const parsed = JSON.parse(text);
      return parsed?.message || parsed?.error || null;
    } catch {
      // Non-JSON body (plain text or an HTML error/redirect page). Only use
      // short bodies so we don't dump a full HTML page into a toast.
      return text.length < 200 ? text : null;
    }
  } catch {
    return null;
  }
};

function* getLocationsSaga({ payload }) {
  try {
    yield put(snowflakeGetLocations.request());
    const client = yield call([SDK, 'getClient']);
    const params = {};
    if (payload?.ignorePre !== undefined) {
      params.ignorePre = payload.ignorePre;
    }
    const { data: response } = yield call(
      [client, 'locationsList'],
      Object.keys(params).length > 0 ? params : undefined
    );
    if (response.success) {
      const mindbodyLocations = response.data.filter((gym) =>
        gym.id.includes('/')
      );
      yield put(snowflakeGetLocations.success(mindbodyLocations));
    } else {
      yield put(snowflakeGetLocations.failure(response.error));
      toast.error(getErrorMessage(response.error, 'Failed to load locations'));
    }
  } catch (error) {
    yield put(snowflakeGetLocations.failure(error));
    toast.error(getErrorMessage(error, 'Failed to load locations'));
  } finally {
    yield put(snowflakeGetLocations.fulfill());
  }
}

function* getStatsSaga() {
  try {
    const studioId = yield select(getSnowflakeSelectedLocation);
    const { startDate, endDate } = yield select(getSnowflakeDateRange);
    if (!studioId || !startDate || !endDate) {
      yield put(snowflakeGetStats.fulfill());
      return;
    }
    yield put(snowflakeGetStats.request());
    const client = yield call([SDK, 'getClient']);
    const { data } = yield call([client, 'getStats'], {
      studioId,
      startDate: format(new Date(startDate), 'yyyy-MM-dd'),
      endDate: format(new Date(endDate), 'yyyy-MM-dd'),
    });
    yield put(snowflakeGetStats.success(data));
  } catch (error) {
    yield put(snowflakeGetStats.failure(error));
    toast.error(getErrorMessage(error, 'Failed to load dashboard stats'));
  } finally {
    yield put(snowflakeGetStats.fulfill());
  }
}

function* getFirstTimersSaga() {
  try {
    const studioId = yield select(getSnowflakeSelectedLocation);
    const { startDate, endDate } = yield select(getSnowflakeDateRange);
    if (!studioId || !startDate || !endDate) {
      yield put(snowflakeGetFirstTimers.fulfill());
      return;
    }
    const locationName = yield select(getSnowflakeSelectedLocationName);
    yield put(snowflakeGetFirstTimers.request());
    const client = yield call([SDK, 'getClient']);
    const formattedStart = format(new Date(startDate), 'yyyy-MM-dd');
    const formattedEnd = format(new Date(endDate), 'yyyy-MM-dd');
    const { data } = yield call([client, 'getFirstTimers'], {
      studioId,
      startDate: formattedStart,
      endDate: formattedEnd,
    });
    if (!Array.isArray(data)) {
      throw new Error(
        'Unexpected response from getFirstTimers (expected an array)'
      );
    }
    yield put(
      snowflakeGetFirstTimers.success({
        items: data,
        // Keep the filters this request actually ran with next to its rows: the
        // picker can move on without refetching, and an export named from the
        // live selection would then mislabel stale data.
        filters: {
          locationName,
          startDate: formattedStart,
          endDate: formattedEnd,
        },
      })
    );
  } catch (error) {
    yield put(snowflakeGetFirstTimers.failure(error));
    toast.error(getErrorMessage(error, 'Failed to load first timers'));
  } finally {
    yield put(snowflakeGetFirstTimers.fulfill());
  }
}

function* getTerminatedMembersSaga() {
  try {
    const studioId = yield select(getSnowflakeSelectedLocation);
    const { startDate, endDate } = yield select(getSnowflakeDateRange);
    if (!studioId || !startDate || !endDate) {
      yield put(snowflakeGetTerminatedMembers.fulfill());
      return;
    }
    const locationName = yield select(getSnowflakeSelectedLocationName);
    yield put(snowflakeGetTerminatedMembers.request());
    const client = yield call([SDK, 'getClient']);
    const formattedStart = format(new Date(startDate), 'yyyy-MM-dd');
    const formattedEnd = format(new Date(endDate), 'yyyy-MM-dd');
    const { data } = yield call([client, 'getTerminatedMembers'], {
      studioId,
      startDate: formattedStart,
      endDate: formattedEnd,
    });
    if (!Array.isArray(data)) {
      throw new Error(
        'Unexpected response from getTerminatedMembers (expected an array)'
      );
    }
    yield put(
      snowflakeGetTerminatedMembers.success({
        items: data,
        // Keep the filters this request actually ran with next to its rows: the
        // picker can move on without refetching, and an export named from the
        // live selection would then mislabel stale data.
        filters: {
          locationName,
          startDate: formattedStart,
          endDate: formattedEnd,
        },
      })
    );
  } catch (error) {
    yield put(snowflakeGetTerminatedMembers.failure(error));
    toast.error(getErrorMessage(error, 'Failed to load terminated members'));
  } finally {
    yield put(snowflakeGetTerminatedMembers.fulfill());
  }
}

// The controls may pass their current, not-yet-applied selection so a download
// doesn't require an Update first; without a payload the applied redux
// selection is used.
function* downloadReportSaga({ payload }) {
  try {
    const studioId =
      payload?.studioIds?.[0] ?? (yield select(getSnowflakeSelectedLocation));
    const { startDate, endDate } =
      payload?.dateRange ?? (yield select(getSnowflakeDateRange));
    if (!studioId || !startDate || !endDate) {
      yield put(snowflakeDownloadReport.fulfill());
      return;
    }
    yield put(snowflakeDownloadReport.request());
    const client = yield call([SDK, 'getClient']);
    const formattedStart = format(new Date(startDate), 'yyyy-MM-dd');
    const formattedEnd = format(new Date(endDate), 'yyyy-MM-dd');
    const response = yield call(
      [client, 'getStudioPerformanceReportPdf'],
      {
        studioId,
        startDate: formattedStart,
        endDate: formattedEnd,
      },
      {},
      { responseType: 'blob' }
    );
    // A blob response can still be an error the server returned with a 2xx
    // status (e.g. a JSON error envelope or an empty body). Downloading that
    // as a `.pdf` would produce a file the user can't open, with no error
    // shown — so verify it's actually a non-empty PDF before saving it.
    const contentType =
      response?.headers?.['content-type'] || response?.data?.type || '';
    if (!contentType.includes('application/pdf') || !response.data?.size) {
      const message = yield call(extractBlobMessage, response.data);
      throw new Error(message || 'The report service did not return a PDF');
    }
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    try {
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `studio-performance-${formattedStart}_${formattedEnd}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      window.URL.revokeObjectURL(url);
    }
    yield put(snowflakeDownloadReport.success());
  } catch (error) {
    yield put(snowflakeDownloadReport.failure(error));
    // On a failed blob request the server's message lives inside the Blob
    // body rather than on `error.response.data`, so read it out first and
    // only fall back to the generic message when nothing usable is found.
    const blobData = error?.response?.data;
    const blobMessage =
      blobData instanceof Blob
        ? yield call(extractBlobMessage, blobData)
        : null;
    toast.error(
      blobMessage || getErrorMessage(error, 'Failed to download the PDF report')
    );
  } finally {
    yield put(snowflakeDownloadReport.fulfill());
  }
}

export default function* snowflakeWatcher() {
  yield all([
    takeLatest(snowflakeGetLocations.TRIGGER, getLocationsSaga),
    takeLatest(snowflakeGetStats.TRIGGER, getStatsSaga),
    takeLatest(snowflakeGetFirstTimers.TRIGGER, getFirstTimersSaga),
    takeLatest(snowflakeGetTerminatedMembers.TRIGGER, getTerminatedMembersSaga),
    takeLatest(snowflakeDownloadReport.TRIGGER, downloadReportSaga),
  ]);
}
